import { useCallback, useEffect, useRef, useState } from "react";
import { useI18n, useMoneyFormatter } from "@expertloop/i18n/react";
import { getLocaleDisplayName, type MessageKey } from "@expertloop/i18n";
import type { AdminMediaFile, AdminMediaVideo, MediationReportClaim, MediationReportContent, MediationReportView } from "@expertloop/shared";
import { adminApi } from "../../services/api";
import { refLabelKey } from "./dossier";
import { CollapsibleCard } from "./CollapsibleCard";
import { MediaLinks } from "./MediaLinks";

/** Délai entre deux lectures tant que le rapport se prépare. */
const POLL_INTERVAL_MS = 4000;

const CLAIM_SECTIONS = ["summary", "commitment", "delivery", "expertBehavior", "clientBehavior", "climate", "finances", "disagreements"] as const;

/** Pièces que le support doit consulter lui-même, que l'IA ne sait pas encore lire. */
export interface ConsultFiles {
	files: AdminMediaFile[];
	videos: AdminMediaVideo[];
	busyId: string | null;
	onOpenFile: (file: AdminMediaFile) => void;
	onOpenVideo: (video: AdminMediaVideo) => void;
}

interface MediationReportPanelProps {
	orderId: string;
	currency: string;
	initial: MediationReportView;
	/** Langue de consultation du dossier, choisie une fois pour toute la page (MEDIATIONUX-3). */
	locale: string;
	/** Signale si un rapport prêt existe dans la langue affichée : le brouillon de réponse en dépend. */
	onReadyChange: (ready: boolean) => void;
	/** Amène la pièce citée dans le fil de la conversation, à droite du rapport. */
	onOpenRef: (ref: string) => void;
	/** Vrai quand le fil contient la pièce citée ; sinon la source n'est pas cliquable. */
	canOpenRef: (ref: string) => boolean;
	/** Pièces à consulter par le support, ouvertes dans le lecteur à côté du rapport. */
	consult: ConsultFiles;
}

function Sources({ refs, onOpenRef, canOpenRef }: { refs: string[]; onOpenRef: (ref: string) => void; canOpenRef: (ref: string) => boolean }) {
	const { t } = useI18n();
	if (refs.length === 0) return null;
	return (
		<p className="mediation-report-sources">
			<span>{t("adminChatDetail.report.sources")} : </span>
			{refs.map((ref, index) => {
				const label = `${t(refLabelKey(ref))} ${index + 1}`;
				return canOpenRef(ref) ? (
					<button key={ref} type="button" className="mediation-source" onClick={() => onOpenRef(ref)}>
						{label}
					</button>
				) : (
					<span key={ref} className="mediation-source">{label}</span>
				);
			})}
		</p>
	);
}

function Claim({ section, claim, onOpenRef, canOpenRef }: { section: (typeof CLAIM_SECTIONS)[number]; claim: MediationReportClaim; onOpenRef: (ref: string) => void; canOpenRef: (ref: string) => boolean }) {
	const { t } = useI18n();
	return (
		<div className="mediation-report-claim">
			<h3>
				{t(`adminChatDetail.report.section.${section}` as MessageKey)}
				<span className={`mediation-chip mediation-chip-${claim.kind}`}>{t(`adminChatDetail.report.kind.${claim.kind}` as MessageKey)}</span>
			</h3>
			<p>{claim.text}</p>
			<Sources refs={claim.refs} onOpenRef={onOpenRef} canOpenRef={canOpenRef} />
		</div>
	);
}

function ReportBody({ content, unreadFiles, currency, onOpenRef, canOpenRef, consult }: { content: MediationReportContent; unreadFiles: string[]; currency: string; onOpenRef: (ref: string) => void; canOpenRef: (ref: string) => boolean; consult: ConsultFiles }) {
	const { t } = useI18n();
	const { formatCents } = useMoneyFormatter();
	const { recommendation } = content;

	return (
		<>
			{CLAIM_SECTIONS.map((section) => (
				<Claim key={section} section={section} claim={content[section]} onOpenRef={onOpenRef} canOpenRef={canOpenRef} />
			))}

			<div className="mediation-report-claim">
				<h3>{t("adminChatDetail.report.concessions.title")}</h3>
				{content.concessions.length === 0 ? (
					<p>{t("adminChatDetail.report.concessions.none")}</p>
				) : (
					<ul>
						{content.concessions.map((concession, index) => (
							<li key={index}>
								{concession.description}
								{concession.amount !== undefined && <> · {t("adminChatDetail.report.concessions.amount", { amount: formatCents(concession.amount, currency) })}</>}
								<Sources refs={concession.refs} onOpenRef={onOpenRef} canOpenRef={canOpenRef} />
							</li>
						))}
					</ul>
				)}
				<span className="mediation-chip mediation-chip-interpretation">{t("adminChatDetail.report.kind.interpretation")}</span>
			</div>

			<div className="mediation-report-claim mediation-report-recommendation">
				<h3>{t("adminChatDetail.report.recommendation.title")}</h3>
				<p>
					<strong>{t(`adminChatDetail.report.recommendation.outcome.${recommendation.outcome}` as MessageKey)}</strong>
					{recommendation.suggestedRefundAmount !== undefined && (
						<> · {t("adminChatDetail.report.recommendation.suggestedAmount", { amount: formatCents(recommendation.suggestedRefundAmount, currency) })}</>
					)}
				</p>
				<p>{recommendation.justification}</p>
				{recommendation.alternatives.length > 0 && (
					<>
						<h4>{t("adminChatDetail.report.recommendation.alternatives")}</h4>
						<ul>
							{recommendation.alternatives.map((alternative, index) => (
								<li key={index}>{alternative}</li>
							))}
						</ul>
					</>
				)}
				<Sources refs={recommendation.refs} onOpenRef={onOpenRef} canOpenRef={canOpenRef} />
			</div>

			<div className="mediation-report-claim">
				<h3>{t("adminChatDetail.report.proposedReply.title")}</h3>
				<blockquote className="mediation-reason-quote">{content.proposedReply}</blockquote>
				<p className="translation-indicative">{t("adminChatDetail.report.proposedReply.note")}</p>
			</div>

			{(consult.files.length > 0 || consult.videos.length > 0 || unreadFiles.length > 0) && (
				<div className="mediation-report-claim mediation-report-consult">
					<h3>{t("adminChatDetail.report.missing.consultTitle")}</h3>
					<p>{t("adminChatDetail.report.missing.consultBody")}</p>
					<MediaLinks files={consult.files} videos={consult.videos} busyId={consult.busyId} onOpenFile={consult.onOpenFile} onOpenVideo={consult.onOpenVideo} />
					{consult.files.length === 0 && unreadFiles.length > 0 && <p>{t("adminChatDetail.report.missing.unreadFiles", { files: unreadFiles.join(", ") })}</p>}
				</div>
			)}

			{content.missingInformation.length > 0 && (
				<div className="mediation-report-claim">
					<h3>{t("adminChatDetail.report.missing.title")}</h3>
					<ul>
						{content.missingInformation.map((item, index) => (
							<li key={index}>{item}</li>
						))}
					</ul>
				</div>
			)}
		</>
	);
}

/**
 * Rapport d'instruction IA (MEDIATIONUX2). Préparé à l'ouverture de la médiation ; le support
 * lit dans la langue de son choix et peut relancer. Un fait cite ses pièces, une interprétation
 * est annoncée comme telle, et l'avis pour l'arbitrage reste une note interne : rien n'est envoyé aux parties.
 */
export function MediationReportPanel({ orderId, currency, initial, locale, onReadyChange, onOpenRef, canOpenRef, consult }: MediationReportPanelProps) {
	const { t } = useI18n();
	const [view, setView] = useState<MediationReportView>(initial);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const requestedLocale = useRef(locale);

	const load = useCallback(
		async (target: string) => {
			try {
				const next = await adminApi.get<MediationReportView>(`/admin/mediations/${orderId}/report?locale=${encodeURIComponent(target)}`);
				// Une réponse arrivée après un changement de langue n'écrase pas la langue choisie depuis
				if (requestedLocale.current === target) setView(next);
			} catch {
				// La lecture suivante réessaie ; le rapport affiché reste celui qu'on avait
			}
		},
		[orderId],
	);

	// La langue de consultation change hors de ce panneau : on lit le rapport de cette langue
	useEffect(() => {
		requestedLocale.current = locale;
		setError("");
		if (locale === initial.locale) {
			setView(initial);
		} else {
			void load(locale);
		}
	}, [locale, initial, load]);

	const generate = async () => {
		setBusy(true);
		setError("");
		try {
			const next = await adminApi.post<MediationReportView>(`/admin/mediations/${orderId}/report`, { locale });
			if (requestedLocale.current === locale) setView(next);
		} catch {
			setError(t("adminChatDetail.report.failed", { code: "request" }));
		} finally {
			setBusy(false);
		}
	};

	const pending = view.report?.status === "pending";
	useEffect(() => {
		if (!pending) return undefined;
		const timer = window.setInterval(() => void load(locale), POLL_INTERVAL_MS);
		return () => window.clearInterval(timer);
	}, [pending, locale, load]);

	const isReady = view.report?.status === "ready" && Boolean(view.report.content);
	useEffect(() => {
		onReadyChange(isReady);
		return () => onReadyChange(false);
	}, [isReady, onReadyChange]);

	// Fonction fermée et aucun rapport : rien à montrer
	if (!view.canGenerate && !view.report) return null;

	const report = view.report;
	const languageName = getLocaleDisplayName(locale, "en");

	return (
		<CollapsibleCard
			className="mediation-report"
			id="mediation-report"
			title={t("adminChatDetail.report.title")}
			subtitle={<p className="translation-indicative">{t("adminChatDetail.report.subtitle")}</p>}
			headerExtra={<span className="mediation-chip" title={t("adminChatDetail.report.language")}>{getLocaleDisplayName(locale, "en")}</span>}
		>
			<div>
				{view.stale && <div className="mediation-report-stale" role="status">{t("adminChatDetail.report.stale")}</div>}
				{error && <div className="resolution-error" role="alert">{error}</div>}

				{!report && <p>{t("adminChatDetail.report.none")}</p>}
				{pending && <p role="status">{t("adminChatDetail.report.generating")}</p>}
				{report?.status === "failed" && (
					<p role="alert">{t("adminChatDetail.report.failed", { code: report.failureCode ?? "unavailable" })}</p>
				)}

				{report?.content && (
					<>
						{report.generatedAt && <p className="translation-indicative">{t("adminChatDetail.report.preparedOn", { date: new Date(report.generatedAt).toLocaleString() })}</p>}
						<ReportBody content={report.content} unreadFiles={report.unreadFiles} currency={currency} onOpenRef={onOpenRef} canOpenRef={canOpenRef} consult={consult} />
					</>
				)}

				{view.canGenerate && !pending && (
					<button type="button" className="btn btn-sm btn-outline" disabled={busy} onClick={() => void generate()}>
						{busy ? t("adminChatDetail.report.generating") : report ? t("adminChatDetail.report.regenerate") : t("adminChatDetail.report.generate", { language: languageName })}
					</button>
				)}
			</div>
		</CollapsibleCard>
	);
}
